/**
 * WelcomeBonusToast — небольшой glass-тост в правом верхнем углу,
 * появляется после завершения онбоардинга и зачисления $1.
 *
 * Слушает event 'luna:welcome-bonus' от OnboardingShell,
 * показывает плашку 6 секунд и уезжает вверх.
 *
 * Текст (админ 17 Apr):
 *   "+$1 USDT"
 *   "Ваш бонус за онбоардинг зачислен"
 *
 * Позиция: под шапкой, правый край (чтобы визуально вёл
 * глаз к balance pill в шапке — баланс юзер видит прирост).
 */
import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { SPRING_CARD } from './springs';

interface BonusEvent {
  amount: string;
}

const TOAST_DURATION_MS = 6000;

export function WelcomeBonusToast() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [amount, setAmount] = useState<string>('1.00');

  useEffect(() => {
    const onBonus = (e: Event) => {
      const ce = e as CustomEvent<BonusEvent>;
      if (ce.detail?.amount) setAmount(ce.detail.amount);
      setVisible(true);
      window.setTimeout(() => setVisible(false), TOAST_DURATION_MS);
    };
    // Если юзер открыл колокольчик — тост уходит, чтобы не
    // перекрывать первую строку уведомлений.
    const onBellOpen = () => setVisible(false);
    window.addEventListener('luna:welcome-bonus', onBonus);
    window.addEventListener('luna:bell-open', onBellOpen);
    return () => {
      window.removeEventListener('luna:welcome-bonus', onBonus);
      window.removeEventListener('luna:bell-open', onBellOpen);
    };
  }, []);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="welcome-bonus-toast"
          initial={{ opacity: 0, y: -20, scale: 0.92 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -10, scale: 0.96 }}
          transition={SPRING_CARD}
          className="fixed z-[8950] pointer-events-none"
          style={{
            top: 'calc(env(safe-area-inset-top, 20px) + 72px)',
            right: 14,
            width: 'min(260px, calc(100vw - 28px))',
          }}
        >
          <div
            className="relative overflow-hidden rounded-[18px] p-3 pl-4"
            style={{
              background:
                'linear-gradient(180deg, rgba(245,230,168,0.18) 0%, rgba(233,199,106,0.12) 100%)',
              backdropFilter: 'blur(24px) saturate(180%)',
              WebkitBackdropFilter: 'blur(24px) saturate(180%)',
              border: '1px solid rgba(234,196,96,0.4)',
              boxShadow:
                '0 18px 38px -12px rgba(234,196,96,0.35), inset 0 1px 0 rgba(255,230,150,0.4)',
            }}
          >
            {/* Золотой shimmer */}
            <motion.div
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  'linear-gradient(120deg, transparent 35%, rgba(255,230,150,0.22) 50%, transparent 65%)',
              }}
              animate={{ x: ['-100%', '120%'] }}
              transition={{
                duration: 1.8,
                ease: 'easeInOut',
              }}
            />
            <div className="relative">
              <p
                className="font-mono"
                style={{
                  fontSize: 9,
                  letterSpacing: '0.25em',
                  textTransform: 'uppercase',
                  color: 'rgba(255,230,180,0.85)',
                }}
              >
                Welcome bonus
              </p>
              <p
                className="mt-0.5"
                style={{
                  fontSize: 22,
                  fontWeight: 800,
                  color: '#f5e6a8',
                  letterSpacing: '-0.02em',
                  textShadow: '0 0 14px rgba(234,196,96,0.55)',
                  fontVariantNumeric: 'tabular-nums',
                  fontFamily:
                    '-apple-system, BlinkMacSystemFont, "SF Pro Display", system-ui, sans-serif',
                }}
              >
                +${amount}
              </p>
              <p
                className="mt-1 text-[11.5px] leading-tight"
                style={{ color: 'rgba(240,230,210,0.8)' }}
              >
                {t('onboarding.bonusToast')}
              </p>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
